import { SignIn, useAuth } from "@mattfinkel/common-web";
import type { ReactNode } from "react";
import { Route, Routes, useLocation } from "react-router";
import { Layout } from "./components/Layout";
import { auth, googleProvider } from "./firebase";
import FlyersWanted from "./pages/FlyersWanted";
import NotFound from "./pages/NotFound";
import People from "./pages/People";
import Roster from "./pages/Roster";
import Shows from "./pages/Shows";
import Stats from "./pages/Stats";
import Upcoming from "./pages/Upcoming";

/** The card shown before the app has an authorized session. */
function Gate({ children }: { children: ReactNode }) {
  return (
    <div className="gate">
      <div className="gate-card">
        <header>
          <div className="brand" style={{ fontSize: 44 }}>
            Shows<i>.</i>
          </div>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Every show you've been to
          </p>
        </header>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const { state, signOut, retry } = useAuth();
  const { pathname } = useLocation();

  // Flyers wanted is PUBLIC: it renders before, and
  // regardless of, the auth state, and its one API route needs no token.
  if (pathname.toLowerCase().replace(/\/$/, "") === "/missing") return <FlyersWanted />;

  // Routes mount ONLY inside the allowed branch, so no page renders for an
  // unauthorized session even on a deep link. The API enforces the same
  // allowlist on every request regardless. Paths match case-insensitively, so
  // older capitalized bookmarks (/Upcoming, /Stats, /Bands) still land.
  if (state.status === "allowed") {
    return (
      <Routes>
        <Route element={<Layout email={state.user.email} onSignOut={signOut} />}>
          <Route index element={<Shows />} />
          <Route path="upcoming" element={<Upcoming />} />
          <Route path="bands" element={<Roster tab="bands" />} />
          <Route path="venues" element={<Roster tab="venues" />} />
          <Route path="stats" element={<Stats />} />
          <Route path="people" element={<People />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Gate>
      {state.status === "idle" && <SignIn auth={auth} provider={googleProvider} />}
      {state.status === "verifying" && <div className="notice">Verifying access…</div>}
      {/* "Could not ask" is not "denied": a cold start keeps the session and offers a retry. */}
      {state.status === "unavailable" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="notice">Could not reach the server: {state.reason}</div>
          <button type="button" className="btn" onClick={retry}>
            Try again
          </button>
        </div>
      )}
      {state.status === "denied" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="notice">Access denied: {state.reason}</div>
          <SignIn auth={auth} provider={googleProvider} />
        </div>
      )}
    </Gate>
  );
}
